import { useSyncExternalStore } from 'react'

export interface RecentEntity {
  type: string
  id: string
  name: string
  /** Route the sidebar links to. */
  path: string
}

const KEY = 'friday.nav.recents'
const MAX = 5

/** Record entity types the sidebar knows how to link back to. */
const PATH_FOR_TYPE: Record<string, (id: string) => string> = {
  contact: (id) => `/contacts/${id}`,
  opportunity: (id) => `/opportunities/${id}`,
  partner: (id) => `/partners/${id}`,
  talent: (id) => `/talent/${id}`,
  club: (id) => `/clubs/${id}`,
  campaign: (id) => `/campaigns/${id}`,
}

function load(): RecentEntity[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as RecentEntity[]) : []
  } catch {
    return []
  }
}

let recents: RecentEntity[] = load()
const listeners = new Set<() => void>()

function emit() {
  for (const l of listeners) l()
}

/**
 * Push a visited record to the front of the recents list. No-op for entity
 * types the sidebar cannot link to (e.g. list "view" entities). Dedupes by
 * type+id and keeps the newest {@link MAX}.
 */
export function recordRecent(entity: { type: string; id: string; name: string }): void {
  const build = PATH_FOR_TYPE[entity.type]
  if (!build || !entity.id || !entity.name) return
  const next: RecentEntity = {
    type: entity.type,
    id: entity.id,
    name: entity.name,
    path: build(entity.id),
  }
  const rest = recents.filter((r) => !(r.type === next.type && r.id === next.id))
  const updated = [next, ...rest].slice(0, MAX)
  // Skip a store churn if nothing actually moved.
  if (updated[0]?.id === recents[0]?.id && updated.length === recents.length) return
  recents = updated
  try {
    localStorage.setItem(KEY, JSON.stringify(recents))
  } catch {
    // Storage full or unavailable — recents stay in memory for this session.
  }
  emit()
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

export function useRecents(): RecentEntity[] {
  return useSyncExternalStore(subscribe, () => recents, () => recents)
}

/** Test-only reset so specs start from a clean store. */
export function __resetRecents(): void {
  recents = []
  try {
    localStorage.removeItem(KEY)
  } catch {
    // ignore
  }
  emit()
}
